import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { buildHotspots } from './buildCentroViewerHotspots';
import './CentroViewer.css';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5001';
// Cambia a 'walk' para más rápido, 'street' para más suave
const TRANSITION_PROFILE = 'street';

// Helper para construir URLs de imágenes correctamente
const buildImageUrl = (ruta) => {
  if (!ruta) return '';
  if (ruta.startsWith('http://') || ruta.startsWith('https://')) return ruta;
  const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const path = ruta.startsWith('/') ? ruta : '/' + ruta;
  return base + path;
};

const MAX_HFOV_DEFAULT = 120;
/** Duración del fundido opacidad (capa captura → panorama nuevo). Independiente del zoom (preLook en hotspots). */
const SCENE_CROSSFADE_MS = 170;

/** Vista guardada en BD (constructor) para al entrar a la escena */
function parseEntradaVistaPanorama(panorama, maxHfov = MAX_HFOV_DEFAULT) {
  if (!panorama?.entrada_vista_json) return null;
  try {
    const j = typeof panorama.entrada_vista_json === 'string'
      ? JSON.parse(panorama.entrada_vista_json)
      : panorama.entrada_vista_json;
    if (j && typeof j.pitch === 'number' && typeof j.yaw === 'number') {
      const hf = Number(j.hfov);
      return { pitch: j.pitch, yaw: j.yaw, hfov: hf > 0 && hf <= 120 ? hf : maxHfov };
    }
  } catch (_) {}
  return null;
}

const CentroViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [centro, setCentro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imagenActual, setImagenActual] = useState(0);
  const [infoTienda, setInfoTienda] = useState(null);
  const [hotspotInfo, setHotspotInfo] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, images: [], currentIndex: 0 });
  const [avisoNo360, setAvisoNo360] = useState(false);
  // Feature: buscador
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  // Feature: plano
  const [showPlano, setShowPlano] = useState(false);
  // Feature: zona toast
  const [zonaLabel, setZonaLabel] = useState('');
  const [showZona, setShowZona] = useState(false);
  const zonaTimerRef = useRef(null);
  // Feature: compartir
  const [copiado, setCopiado] = useState(false);
  // Ref miniaturas para scroll-into-view
  const miniaturasRef = useRef(null);

  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(prev => ({ ...prev, open: false }));
      if (e.key === 'ArrowLeft') setLightbox(prev => ({ ...prev, currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length }));
      if (e.key === 'ArrowRight') setLightbox(prev => ({ ...prev, currentIndex: (prev.currentIndex + 1) % prev.images.length }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox.open, lightbox.images.length]);
  const [visorOpacity, setVisorOpacity] = useState(1);
  const afterFirstPanoramaLoadRef = useRef(false);
  const hasLeftInitialSceneRef = useRef(false);
  const [panoramaCargado, setPanoramaCargado] = useState(false);
  const [pannellumReady, setPannellumReady] = useState(!!window.pannellum);
  const nextViewRef = useRef(null);
  const viewerRef = useRef(null);
  const pannellumRef = useRef(null);
  /** Evita ráfagas de resize/setUpdate que pueden perder el contexto WebGL y descolocar hotspots */
  const hotspotRevealDebounceRef = useRef(null);
  const hotspotRefreshDebounceRef = useRef(null);
  const hotspotsRevealAllowedRef = useRef(true);

  useEffect(() => {
    document.body.classList.add('route-viewer');
    return () => document.body.classList.remove('route-viewer');
  }, []);

  // Resultados de búsqueda derivados
  const panoramasList = centro?.panoramas || [];
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || !centro) return [];
    const q = searchQuery.toLowerCase().trim();
    const results = [];
    panoramasList.forEach((p, idx) => {
      const label = p.nombre || p.planta_nombre || '';
      const sublabel = (p.nombre && p.planta_nombre) ? p.planta_nombre : '';
      if (label.toLowerCase().includes(q))
        results.push({ tipo: 'zona', label, sublabel, idx, icon: '🔭' });
    });
    const plantaDeIdx = (pIdx) => panoramasList[pIdx]?.planta_nombre || '';
    (centro.hotspots || []).forEach(h => {
      const label = (h.titulo || h.nombre || '').trim();
      if (!label.toLowerCase().includes(q)) return;
      const pIdx = panoramasList.findIndex(p => Number(p.id) === Number(h.panorama_id));
      if (pIdx < 0) return;
      const esLogo = String(h.css_class || '').includes('caja-logo');
      results.push({ tipo: esLogo ? 'tienda' : 'punto', label, sublabel: plantaDeIdx(pIdx), idx: pIdx, icon: esLogo ? '🏪' : '📍' });
    });
    (centro.tiendas || []).forEach(t => {
      if (!t.nombre?.toLowerCase().includes(q)) return;
      const pIdx = panoramasList.findIndex(p => Number(p.id) === Number(t.panorama_id));
      if (pIdx < 0) return;
      results.push({ tipo: 'tienda', label: t.nombre, sublabel: plantaDeIdx(pIdx), idx: pIdx, icon: '🏬' });
    });
    const seen = new Set();
    return results.filter(r => { const k = r.idx + ':' + r.label; if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, 8);
  }, [searchQuery, centro, panoramasList]);

  // Scroll de miniatura activa a la vista
  useEffect(() => {
    if (!miniaturasRef.current) return;
    const el = miniaturasRef.current.querySelector('.miniatura.activa');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [imagenActual]);

  useEffect(() => {
    const cargarCentro = async () => {
      try {
        const res = await api.get(`/api/centros/${id}`, { params: { _t: Date.now() } });
        if (res.data.success) {
          setCentro(res.data.centro);
          setImagenActual(0);
        } else {
          setError('Centro no encontrado');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error al cargar el centro');
      } finally {
        setLoading(false);
      }
    };

    let pollId = null;
    const existingScript = document.querySelector('script[src*="pannellum"]');
    if (!existingScript) {
      const s = document.createElement('script');
      s.src = '/pannellum.js';
      s.async = true;
      s.onload = () => setPannellumReady(true);
      document.head.appendChild(s);
    } else if (window.pannellum) {
      setPannellumReady(true);
    } else {
      pollId = setInterval(() => {
        if (window.pannellum) {
          setPannellumReady(true);
          clearInterval(pollId);
          pollId = null;
        }
      }, 80);
      setTimeout(() => { if (pollId) { clearInterval(pollId); pollId = null; } }, 5000);
    }
    cargarCentro();
    return () => {
      if (pollId) clearInterval(pollId);
      if (pannellumRef.current) {
        try {
          pannellumRef.current.destroy();
        } catch (e) {
          // Ignorar errores al destruir
        }
      }
    };
  }, [id]);

  useEffect(() => {
    if (!pannellumReady || !window.pannellum || !centro?.panoramas?.length || !viewerRef.current || loading) return;

    const panoramas = centro.panoramas;
    if (panoramas.length === 0) return;

    panoramas.forEach((p) => {
      const u = buildImageUrl(p?.ruta_archivo);
      if (!u) return;
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.src = u;
    });

    setPanoramaCargado(false);
    afterFirstPanoramaLoadRef.current = false;
    hasLeftInitialSceneRef.current = false;
    if (pannellumRef.current) {
      try { pannellumRef.current.destroy(); } catch (e) {}
      pannellumRef.current = null;
    }
    viewerRef.current.innerHTML = '';
    hotspotsRevealAllowedRef.current = true;

    /** Un solo repintado: resize + lookAt inmediato con la vista actual (sin setUpdate, menos estrés en WebGL). */
    const syncHotspotLayoutOnce = (v) => {
      if (!v) return;
      try {
        if (typeof v.resize === 'function') v.resize();
        if (typeof v.lookAt === 'function' && typeof v.getPitch === 'function') {
          const pitch = v.getPitch();
          const yaw = v.getYaw();
          const hfov = v.getHfov();
          v.lookAt(pitch, yaw, hfov, 0);
        }
      } catch (_) {}
    };

    /** Recalcula posiciones tras la carga inicial (offsetWidth/Height ya tienen valor real). */
    const runRevealHotspotsAfterLayout = () => {
      const v = pannellumRef.current;
      if (!v) return;
      syncHotspotLayoutOnce(v);
      setTimeout(() => {
        syncHotspotLayoutOnce(v);
        try { forceHotspotsVisible(); } catch (_) {}
      }, 150);
    };

    /** Tras cambio de escena: recalcular posiciones de hotspots. */
    const runLightHotspotSyncAfterTransition = () => {
      const v = pannellumRef.current;
      if (!v) return;
      syncHotspotLayoutOnce(v);
      requestAnimationFrame(() => {
        syncHotspotLayoutOnce(v);
        try { forceHotspotsVisible(); } catch (_) {}
      });
    };

    const scheduleRevealHotspotsAfterLayout = () => {
      if (hotspotRevealDebounceRef.current) clearTimeout(hotspotRevealDebounceRef.current);
      hotspotRevealDebounceRef.current = setTimeout(() => {
        hotspotRevealDebounceRef.current = null;
        runRevealHotspotsAfterLayout();
      }, 120);
    };

    /** Timeout de respaldo si Pannellum no emite scenechangefadedone (p. ej. sin capa fade). */
    let pendingPostLoadHotspotSync = null;

    /** Cuando un hotspot termina de cargar imágenes (logo, stand), recalcular posiciones sin setUpdate. */
    const refreshHotspotPositions = () => {
      if (hotspotRefreshDebounceRef.current) clearTimeout(hotspotRefreshDebounceRef.current);
      hotspotRefreshDebounceRef.current = setTimeout(() => {
        hotspotRefreshDebounceRef.current = null;
        const v = pannellumRef.current;
        if (!v || !viewerRef.current) return;
        syncHotspotLayoutOnce(v);
        requestAnimationFrame(() => {
          syncHotspotLayoutOnce(v);
          try { forceHotspotsVisible(); } catch (_) {}
        });
      }, 200);
    };

    const forceHotspotsVisible = () => {
      if (!viewerRef.current) return;
      /* IMPORTANTE: NO tocar visibility ni opacity — Ca() de Pannellum los controla.
         Ca() solo corre durante animación; si borramos su inline al pararse el loop,
         el CSS default de Pannellum (visibility:hidden) dejaría los hotspots ocultos. */
      viewerRef.current.querySelectorAll('.pnlm-hotspot-base, .pnlm-hotspot').forEach((el) => {
        if (!el?.style) return;
        if (el.classList.contains('hotspot-valla') || el.classList.contains('hotspot-caja-logo') || el.classList.contains('hotspot-persona')) {
          el.style.setProperty('pointer-events', 'none', 'important');
        } else {
          el.style.setProperty('pointer-events', 'auto', 'important');
        }
        el.style.setProperty('display', 'flex', 'important');
        if (el.classList.contains('hotspot-flecha-pulse')) {
          el.style.setProperty('min-width', '72px', 'important');
          el.style.setProperty('min-height', '72px', 'important');
        }
        if (el.classList.contains('hotspot-caja-logo')) {
          el.querySelectorAll('.logo-stand-shell, .logo-stand-bg-img, .logo-stand-base').forEach((n) => {
            if (n?.style) n.style.setProperty('pointer-events', 'none', 'important');
          });
          el.querySelectorAll('.logo-stand-fascia, .hotspot-caja-img').forEach((n) => {
            if (n?.style) n.style.setProperty('pointer-events', 'auto', 'important');
          });
        }
        if (el.classList.contains('hotspot-persona')) {
          el.style.setProperty('align-items', 'flex-end', 'important');
          el.style.setProperty('justify-content', 'center', 'important');
          el.querySelectorAll('.logo-persona-contenido').forEach((n) => {
            if (n?.style) n.style.setProperty('pointer-events', 'auto', 'important');
          });
        }
      });
    };

    /** Sin atenuar el contenedor: eso generaba parpadeo negro encima del fundido nativo de Pannellum */
    const onNavigateToScene = (destIdx) => {
      setVisorOpacity(1);
      setImagenActual(destIdx);
    };

    const maxHfov = MAX_HFOV_DEFAULT;

    const getSceneEntryView = (destPanoramaId, fromPanoramaId = null) => {
      const destPanorama = panoramas.find((p) => Number(p.id) === Number(destPanoramaId));
      const saved = destPanorama ? parseEntradaVistaPanorama(destPanorama, maxHfov) : null;
      if (saved) {
        return saved;
      }
      const allHotspots = (centro.hotspots || []);
      const inDest = allHotspots.filter(h => Number(h.panorama_id) === Number(destPanoramaId));
      // Prioriza hotspot de retorno: destino -> origen (sensación de continuidad tipo Street View)
      if (fromPanoramaId != null) {
        const back = inDest.find(h => {
          const css = String(h.css_class || '').toLowerCase();
          const tipo = String(h.tipo || '').toLowerCase();
          if (css.includes('hotspot-valla') || tipo === 'valla') return false;
          return Number(h.panorama_destino_id) === Number(fromPanoramaId);
        });
        if (back) {
          return {
            pitch: Number(back.pitch) || 0,
            yaw: Number(back.yaw) || 0,
            hfov: maxHfov
          };
        }
      }
      // Excluir vallas para centrar en flechas/navegación (igual que Constructor y vista inicial)
      const inDestSinValla = inDest.filter(h => {
        const css = String(h.css_class || '').toLowerCase();
        const tipo = String(h.tipo || '').toLowerCase();
        return !css.includes('hotspot-valla') && tipo !== 'valla';
      });
      const refs = inDestSinValla.length > 0 ? inDestSinValla : inDest;
      if (refs.length > 0) {
        const sumPitch = refs.reduce((s, h) => s + (Number(h.pitch) || 0), 0);
        const sumYaw = refs.reduce((s, h) => s + (Number(h.yaw) || 0), 0);
        return { pitch: sumPitch / refs.length, yaw: sumYaw / refs.length, hfov: maxHfov };
      }
      return { pitch: 0, yaw: 0, hfov: maxHfov };
    };

    // Feature compartir: leer panorama inicial desde URL (?panorama=N)
    const panoParam = new URLSearchParams(window.location.search).get('panorama');
    const startIdx = panoParam !== null && !isNaN(parseInt(panoParam, 10))
      ? Math.max(0, Math.min(parseInt(panoParam, 10), panoramas.length - 1))
      : 0;
    if (startIdx > 0) setImagenActual(startIdx);

    const scenes = {};
    panoramas.forEach((panorama, idx) => {
      const hotspotsDeImagen = (centro.hotspots || []).filter(h => Number(h.panorama_id) === Number(panorama.id));
      const tiendasDeImagen = (centro.tiendas || []).filter(t => t.panorama_id === panorama.id);
      const hotSpots = buildHotspots({
        buildImageUrl,
        viewerRef,
        pannellumRef,
        setImagenActual,
        setInfoTienda,
        setHotspotInfo,
        onNavigateToScene,
        getSceneEntryView,
        maxHfov,
        transitionProfile: TRANSITION_PROFILE,
        isMultiScene: true,
        centro,
        panoramas,
        hotspotsDeImagen,
        tiendasDeImagen,
        forceHotspotsVisible,
        onHotspotLayoutRefresh: refreshHotspotPositions
      });
      const sceneObj = {
        type: 'equirectangular',
        panorama: buildImageUrl(panorama.ruta_archivo),
        autoLoad: true,
        hotSpots,
        /* Asegura duración de fundido al cambiar de escena (H() fusiona por escena) */
        sceneFadeDuration: SCENE_CROSSFADE_MS
      };
      const entradaScene = parseEntradaVistaPanorama(panorama, maxHfov);
      if (entradaScene) {
        sceneObj.pitch = entradaScene.pitch;
        sceneObj.yaw = entradaScene.yaw;
        sceneObj.hfov = entradaScene.hfov;
      }
      scenes['scene_' + idx] = sceneObj;
    });

    // Vista inicial: si hay vista guardada en la escena, si no media de flechas (sin vallas)
    const hotspotsPrimera = (centro.hotspots || []).filter(h => {
      if (Number(h.panorama_id) !== Number(panoramas[0]?.id)) return false;
      const css = String(h.css_class || '').toLowerCase();
      const tipo = String(h.tipo || '').toLowerCase();
      if (css.includes('hotspot-valla') || tipo === 'valla') return false;
      return true;
    });
    const entradaPrimera = panoramas[0] ? parseEntradaVistaPanorama(panoramas[0], maxHfov) : null;
    let initialPitch = 0;
    let initialYaw = 0;
    let initialHfov = maxHfov;
    if (entradaPrimera) {
      initialPitch = entradaPrimera.pitch;
      initialYaw = entradaPrimera.yaw;
      initialHfov = entradaPrimera.hfov;
    } else if (hotspotsPrimera.length > 0) {
      const sumPitch = hotspotsPrimera.reduce((s, h) => s + (Number(h.pitch) || 0), 0);
      const sumYaw = hotspotsPrimera.reduce((s, h) => s + (Number(h.yaw) || 0), 0);
      initialPitch = sumPitch / hotspotsPrimera.length;
      initialYaw = sumYaw / hotspotsPrimera.length;
    }
    const tourConfig = {
      default: {
        firstScene: 'scene_' + startIdx,
        sceneFadeDuration: SCENE_CROSSFADE_MS,
        /* RGB 0–1, alineado con #111 del contenedor para menos contraste en bordes del fundido */
        backgroundColor: [17 / 255, 17 / 255, 17 / 255],
        compass: true,
        showControls: true,
        pitch: initialPitch,
        yaw: initialYaw,
        hfov: initialHfov,
        minHfov: 50,
        maxHfov,
        showFullscreenCtrl: true
      },
      scenes
    };

    let fallbackId = null;
    try {
      pannellumRef.current = window.pannellum.viewer(viewerRef.current, tourConfig);
      pannellumRef.current.on('scenechange', (sceneId) => {
        const idx = parseInt(String(sceneId).replace('scene_', ''), 10);
        if (!Number.isNaN(idx)) {
          setImagenActual(idx);
          // Feature zona: mostrar nombre del panorama (el que el admin configura en constructor)
          const nombre = panoramas[idx]?.nombre || panoramas[idx]?.planta_nombre;
          if (nombre) {
            setZonaLabel(nombre);
            setShowZona(true);
            if (zonaTimerRef.current) clearTimeout(zonaTimerRef.current);
            zonaTimerRef.current = setTimeout(() => setShowZona(false), 3000);
          }
        }
        setHotspotInfo(null);
        const idStr = String(sceneId);
        if (idStr !== 'scene_0') hasLeftInitialSceneRef.current = true;
      });
      pannellumRef.current.on('scenechangefadedone', () => {
        if (pendingPostLoadHotspotSync != null) {
          clearTimeout(pendingPostLoadHotspotSync);
          pendingPostLoadHotspotSync = null;
        }
        runLightHotspotSyncAfterTransition();
      });
      pannellumRef.current.on('zoomchange', () => setHotspotInfo(null));
      pannellumRef.current.on('animatefinished', () => {
        setHotspotInfo(null);
        try {
          const v = pannellumRef.current;
          if (v && typeof v.resize === 'function') v.resize();
          forceHotspotsVisible();
        } catch (_) {}
      });
      // Fallback: si tras 12 s no carga, ocultar overlay para evitar pantalla negra infinita
      fallbackId = setTimeout(() => {
        setPanoramaCargado((prev) => (prev ? prev : true));
      }, 12000);
      pannellumRef.current.on('load', () => {
        clearTimeout(fallbackId);
        fallbackId = null;
        setPanoramaCargado(true);
        setVisorOpacity(1);
        afterFirstPanoramaLoadRef.current = true;
        scheduleRevealHotspotsAfterLayout();
      });
    } catch (err) {
      console.error(err);
      setError('Error al cargar el visor 360');
    }

    return () => {
      if (fallbackId != null) clearTimeout(fallbackId);
      if (pendingPostLoadHotspotSync != null) {
        clearTimeout(pendingPostLoadHotspotSync);
        pendingPostLoadHotspotSync = null;
      }
      if (hotspotRevealDebounceRef.current) {
        clearTimeout(hotspotRevealDebounceRef.current);
        hotspotRevealDebounceRef.current = null;
      }
      if (hotspotRefreshDebounceRef.current) {
        clearTimeout(hotspotRefreshDebounceRef.current);
        hotspotRefreshDebounceRef.current = null;
      }
    };
  }, [centro, loading, pannellumReady]);

  if (loading) return <div className="visor-loading">Cargando recorrido...</div>;
  if (error || !centro) {
    return (
      <div className="visor-error">
        <p>{error || 'Centro no encontrado'}</p>
        <button onClick={() => navigate('/')}>Volver al Globe</button>
      </div>
    );
  }

  if (!centro.panoramas?.length) {
    return (
      <div className="visor-error">
        <p>Este centro aún no tiene panoramas. Configura imágenes desde el panel de administración.</p>
        <button onClick={() => navigate('/')}>Volver al Globe</button>
      </div>
    );
  }

  const plantas = centro.plantas || [];
  const panoramas = centro.panoramas || [];
  const panoramaActual = panoramas[imagenActual];

  const handleCompartir = () => {
    const url = window.location.origin + '/centro/' + id + '?panorama=' + imagenActual;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => {});
    } else {
      const el = document.createElement('textarea');
      el.value = url; document.body.appendChild(el); el.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(el);
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  const goToScene = (idx) => {
    if (idx === imagenActual) return;
    if (!panoramas[idx]) return;
    const maxHfov = MAX_HFOV_DEFAULT;
    const targetPanorama = panoramas[idx];
    const entrada = parseEntradaVistaPanorama(targetPanorama, maxHfov);
    let pitch = 0;
    let yaw = 0;
    let hfov = maxHfov;
    if (entrada) {
      pitch = entrada.pitch;
      yaw = entrada.yaw;
      hfov = entrada.hfov;
    } else {
      const inDest = (centro?.hotspots || []).filter(h => Number(h.panorama_id) === Number(targetPanorama?.id));
      const sinValla = inDest.filter((h) => {
        const css = String(h.css_class || '').toLowerCase();
        const tipo = String(h.tipo || '').toLowerCase();
        return !css.includes('hotspot-valla') && tipo !== 'valla';
      });
      const refs = sinValla.length > 0 ? sinValla : inDest;
      if (refs.length > 0) {
        pitch = refs.reduce((s, h) => s + (Number(h.pitch) || 0), 0) / refs.length;
        yaw = refs.reduce((s, h) => s + (Number(h.yaw) || 0), 0) / refs.length;
      }
    }
    if (pannellumRef.current && typeof pannellumRef.current.loadScene === 'function') {
      pannellumRef.current.loadScene('scene_' + idx, pitch, yaw, hfov);
    }
    setImagenActual(idx);
  };

  return (
    <div className="centro-viewer">
      <div className="visor-wrapper" style={{ opacity: visorOpacity, transition: 'opacity 0.35s ease-in-out' }}>
        <div ref={viewerRef} id="panorama" className="panorama-viewer" />
        {!panoramaCargado && (
          <div className="visor-cargando-panorama">
            <div className="visor-cargando-spinner" />
            <span>Cargando imagen 360°...</span>
          </div>
        )}
      </div>

      {/* Botón flotante volver al Globe - estilo Simple Virtual Tour */}
      <button className="btn-return-globe" onClick={() => navigate('/')} title="Volver al Globe">
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
      </button>

      {/* Barra superior con buscador y compartir */}
      <div className="visor-overlay-top">
        <span className="visor-overlay-title">{centro.nombre}</span>
        {panoramaActual && (
          <span className="planta-badge">{panoramaActual.planta_nombre || 'Recorrido'}</span>
        )}
        {avisoNo360 && (
          <span className="visor-aviso-no360" title="Vista parcial">Vista parcial</span>
        )}
        <div className="visor-top-actions">
          {/* Buscador */}
          <div className={`visor-search-wrap ${searchOpen ? 'open' : ''}`}>
            {searchOpen && (
              <input
                ref={searchInputRef}
                className="visor-search-input"
                type="text"
                placeholder="Buscar tienda o zona..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); } }}
                autoFocus
              />
            )}
            <button className="visor-top-btn" title="Buscar tienda o zona"
              onClick={() => { setSearchOpen(o => !o); setSearchQuery(''); }}>
              🔍
            </button>
            {searchOpen && searchResults.length > 0 && (
              <div className="visor-search-results">
                {searchResults.map((r, i) => (
                  <div key={i} className="visor-search-item" onClick={() => { goToScene(r.idx); setSearchOpen(false); setSearchQuery(''); }}>
                    <span className="visor-search-icon">{r.icon}</span>
                    <span className="visor-search-label">{r.label}</span>
                    {r.sublabel && <span className="visor-search-sub">{r.sublabel}</span>}
                  </div>
                ))}
              </div>
            )}
            {searchOpen && searchQuery.trim() && searchResults.length === 0 && (
              <div className="visor-search-results">
                <div className="visor-search-empty">Sin resultados</div>
              </div>
            )}
          </div>
          {/* Compartir */}
          <div className="visor-share-wrap">
            <button className="visor-top-btn" title="Copiar enlace a esta vista" onClick={handleCompartir}>
              🔗
            </button>
            {copiado && <span className="visor-copiado">¡Enlace copiado!</span>}
          </div>
        </div>
      </div>

      {/* Toast nombre de zona al cambiar de escena */}
      <div className={`visor-zona-toast ${showZona ? 'visible' : ''}`}>
        <span className="visor-zona-icon">📍</span>
        <span className="visor-zona-label">{zonaLabel}</span>
      </div>

      {/* Controles inferiores tipo SVT */}
      <div className="visor-overlay-bottom">
        <div className="visor-nav">
          <button onClick={() => goToScene(Math.max(0, imagenActual - 1))} disabled={imagenActual === 0}>←</button>
          <span>{imagenActual + 1} / {panoramas.length}</span>
          <button onClick={() => goToScene(Math.min(panoramas.length - 1, imagenActual + 1))} disabled={imagenActual === panoramas.length - 1}>→</button>
        </div>

        {plantas.length > 1 && (
          <div className="plantas-selector">
            {plantas.map((pl) => (
              <button
                key={pl.id}
                className={(panoramaActual?.planta_id === pl.id) ? 'active' : ''}
                onClick={() => {
                  const firstInPlanta = panoramas.findIndex(p => p.planta_id === pl.id);
                  if (firstInPlanta >= 0) goToScene(firstInPlanta);
                }}
              >
                {pl.nombre}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Miniaturas colapsables */}
      <div className="visor-miniaturas" ref={miniaturasRef}>
        {panoramas.map((p, idx) => (
          <div
            key={p.id}
            className={`miniatura ${idx === imagenActual ? 'activa' : ''}`}
            onClick={() => goToScene(idx)}
            title={p.nombre || `Vista ${idx + 1}`}
          >
            <img src={buildImageUrl(p.ruta_archivo)} alt={p.nombre} onError={(e) => e.target.style.display = 'none'} />
            {idx === imagenActual && <span className="miniatura-aqui">📍</span>}
          </div>
        ))}
      </div>

      {/* Botón plano flotante */}
      <button className="btn-plano-toggle" onClick={() => setShowPlano(true)} title="Plano del centro">
        🗺️
      </button>

      {/* Modal plano */}
      {showPlano && (
        <div className="plano-overlay" onClick={() => setShowPlano(false)}>
          <div className="plano-modal" onClick={e => e.stopPropagation()}>
            <div className="plano-modal-header">
              <h3 className="plano-title">🗺️ {centro.nombre}</h3>
              <button className="modal-close" onClick={() => setShowPlano(false)}>×</button>
            </div>
            {plantas.length > 1 ? (
              plantas.map(pl => (
                <div key={pl.id} className="plano-planta-section">
                  <h4 className="plano-planta-label">{pl.nombre}</h4>
                  <div className="plano-panoramas-grid">
                    {panoramas.filter(p => p.planta_id === pl.id).map(p => {
                      const pIdx = panoramas.findIndex(pp => pp.id === p.id);
                      const isActual = pIdx === imagenActual;
                      return (
                        <div key={p.id} className={`plano-pano-card ${isActual ? 'plano-activo' : ''}`}
                          onClick={() => { goToScene(pIdx); setShowPlano(false); }}>
                          <div className="plano-pano-thumb">
                            <img src={buildImageUrl(p.ruta_archivo)} alt={p.nombre} onError={e => e.target.style.display = 'none'} />
                            {isActual && <span className="plano-aqui-badge">📍 Aquí</span>}
                          </div>
                          <span className="plano-pano-name">{p.nombre || p.planta_nombre || `Vista ${pIdx + 1}`}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="plano-panoramas-grid">
                {panoramas.map((p, pIdx) => {
                  const isActual = pIdx === imagenActual;
                  return (
                    <div key={p.id} className={`plano-pano-card ${isActual ? 'plano-activo' : ''}`}
                      onClick={() => { goToScene(pIdx); setShowPlano(false); }}>
                      <div className="plano-pano-thumb">
                        <img src={buildImageUrl(p.ruta_archivo)} alt={p.nombre || p.planta_nombre} onError={e => e.target.style.display = 'none'} />
                        {isActual && <span className="plano-aqui-badge">📍 Aquí</span>}
                      </div>
                      <span className="plano-pano-name">{p.nombre || p.planta_nombre || `Vista ${pIdx + 1}`}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal info tienda */}
      {infoTienda && (
        <div className="modal-tienda-overlay" onClick={() => setInfoTienda(null)}>
          <div className="modal-tienda" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setInfoTienda(null)}>×</button>
            <h3>{infoTienda.nombre}</h3>
            {infoTienda.categoria && <p className="categoria">{infoTienda.categoria}</p>}
            {infoTienda.descripcion && <p>{infoTienda.descripcion}</p>}
            {infoTienda.telefono && <p>📞 {infoTienda.telefono}</p>}
            {infoTienda.web && <a href={infoTienda.web} target="_blank" rel="noopener noreferrer">Visitar web</a>}
          </div>
        </div>
      )}

      {/* Modal información de hotspot (punto / logo) al hacer clic: se abre anclado al hotspot con efecto */}
      {hotspotInfo && (
        <div className="modal-tienda-overlay modal-hotspot-info-overlay" onClick={() => setHotspotInfo(null)}>
          <div
            className={`modal-tienda modal-hotspot-info hotspot-popover ${hotspotInfo.rect ? 'hotspot-popover-anchored' : ''}`}
            style={hotspotInfo.rect ? {
              left: hotspotInfo.rect.left + hotspotInfo.rect.width / 2,
              top: hotspotInfo.rect.top + hotspotInfo.rect.height / 2
            } : undefined}
            onClick={e => e.stopPropagation()}
          >
            <button className="modal-close" onClick={() => setHotspotInfo(null)} aria-label="Cerrar">×</button>
            <div
              className="hotspot-popover-inner"
              onClick={(e) => {
                const img = e.target.closest('img[data-galeria-urls]');
                if (img) {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const urls = JSON.parse(img.getAttribute('data-galeria-urls').replace(/&quot;/g, '"'));
                    const idx = parseInt(img.getAttribute('data-galeria-index') || '0', 10);
                    setLightbox({ open: true, images: urls, currentIndex: idx });
                  } catch (_) {}
                }
              }}
              dangerouslySetInnerHTML={{ __html: typeof hotspotInfo === 'string' ? hotspotInfo : hotspotInfo.html }}
            />
          </div>
        </div>
      )}

      {/* Lightbox galería */}
      {lightbox.open && lightbox.images.length > 0 && (
        <div className="hotspot-lightbox-overlay" onClick={() => setLightbox(prev => ({ ...prev, open: false }))}>
          <button type="button" className="hotspot-lightbox-close" onClick={() => setLightbox(prev => ({ ...prev, open: false }))} aria-label="Cerrar">×</button>
          <div className="hotspot-lightbox-content" onClick={e => e.stopPropagation()}>
            <img src={lightbox.images[lightbox.currentIndex]} alt="" className="hotspot-lightbox-img" />
            {lightbox.images.length > 1 && (
              <>
                <button type="button" className="hotspot-lightbox-prev" onClick={(e) => { e.stopPropagation(); setLightbox(prev => ({ ...prev, currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length })); }} aria-label="Anterior">‹</button>
                <button type="button" className="hotspot-lightbox-next" onClick={(e) => { e.stopPropagation(); setLightbox(prev => ({ ...prev, currentIndex: (prev.currentIndex + 1) % prev.images.length })); }} aria-label="Siguiente">›</button>
                <span className="hotspot-lightbox-counter">{lightbox.currentIndex + 1} / {lightbox.images.length}</span>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default CentroViewer;
